/* ==========================================================================
   Blink Box - Product Figures Gallery analytics
   Markup source: snippets/product-specs_figures-gallery.liquid
========================================================================== */
(function () {
  'use strict';

  if (window.__blinkboxFiguresGalleryAnalyticsLoaded) return;
  window.__blinkboxFiguresGalleryAnalyticsLoaded = true;

  const SELECTORS = {
    root: '[data-product-specs-figures-gallery]',
    track: '[data-product-specs-figures-gallery-track]',
    previous: '[data-product-specs-figures-gallery-prev]',
    next: '[data-product-specs-figures-gallery-next]'
  };

  const initialised = new WeakSet();
  const viewTracked = new WeakSet();
  const viewTimers = new WeakMap();
  const interactionStates = new WeakMap();

  function publishShopifyEvent(name, payload) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    try {
      const result = window.Shopify.analytics.publish(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      // Analytics must never interrupt the shopping experience.
    }
  }

  function sendGoogleEvent(name, params) {
    if (typeof window.gtag !== 'function') return;

    try {
      window.gtag('event', name, params);
    } catch (error) {
      // Google measurement is optional.
    }
  }

  function getMaxScroll(track) {
    return Math.max(track.scrollWidth - track.clientWidth, 0);
  }

  function hasOverflow(track) {
    return getMaxScroll(track) > 2;
  }

  function buildBasePayload(root) {
    const track = root.querySelector(SELECTORS.track);

    return {
      vendor: root.dataset.sourceProductVendor || '',
      source_product_id: root.dataset.sourceProductId || '',
      source_product_name: root.dataset.sourceProductName || '',
      source_product_handle: root.dataset.sourceProductHandle || '',
      lineup_name: root.dataset.lineupName || '',
      gallery_heading: root.dataset.galleryHeading || '',
      figure_count: Number(root.dataset.figureCount || 0),
      regular_figure_count: Number(root.dataset.regularFigureCount || 0),
      secret_figure_count: Number(root.dataset.secretFigureCount || 0),
      has_overflow: track ? String(hasOverflow(track)) : 'false'
    };
  }

  function track(root, eventName, extra = {}) {
    const payload = {
      ...buildBasePayload(root),
      ...extra
    };

    publishShopifyEvent(`blinkbox_${eventName}`, payload);
    sendGoogleEvent(eventName, payload);
  }

  function trackView(root) {
    if (viewTracked.has(root)) return;

    const timer = viewTimers.get(root);
    if (timer) window.clearTimeout(timer);

    viewTimers.delete(root);
    viewTracked.add(root);
    track(root, 'figures_gallery_view');
  }

  function initViewTracking(root) {
    const track = root.querySelector(SELECTORS.track);
    if (!track || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (viewTracked.has(root)) {
          observer.disconnect();
          return;
        }

        if (entry.intersectionRatio >= 0.01) {
          if (viewTimers.has(root)) return;

          const timer = window.setTimeout(() => {
            trackView(root);
            observer.disconnect();
          }, 250);

          viewTimers.set(root, timer);
        } else {
          const timer = viewTimers.get(root);
          if (timer) window.clearTimeout(timer);
          viewTimers.delete(root);
        }
      });
    }, { threshold: [0, 0.01] });

    observer.observe(track);
  }

  function canMove(track, direction) {
    const maxScroll = getMaxScroll(track);
    if (maxScroll <= 2) return false;

    if (direction === 'previous') return track.scrollLeft > 2;
    return track.scrollLeft < maxScroll - 2;
  }

  function trackEngage(root, method, direction) {
    const state = interactionStates.get(root);
    if (!state || state.engaged) return;

    if (!viewTracked.has(root)) trackView(root);

    state.engaged = true;
    state.pendingMethod = '';

    track(root, 'figures_gallery_engage', {
      interaction_method: method,
      interaction_direction: direction
    });
  }

  function setPendingInteraction(root, track, method) {
    const state = interactionStates.get(root);
    if (!state || state.engaged) return;

    state.pendingMethod = method;
    state.scrollAnchor = track.scrollLeft;
  }

  function handleScrollInteraction(root, track) {
    const state = interactionStates.get(root);
    if (!state || state.engaged || !state.pendingMethod) return;

    const delta = track.scrollLeft - state.scrollAnchor;
    if (Math.abs(delta) < 12) return;

    trackEngage(
      root,
      state.pendingMethod,
      delta > 0 ? 'next' : 'previous'
    );
  }

  function initInteractionTracking(root) {
    const track = root.querySelector(SELECTORS.track);
    if (!track) return;

    interactionStates.set(root, {
      engaged: false,
      pendingMethod: '',
      scrollAnchor: track.scrollLeft
    });

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const dot = event.target.closest('[data-product-specs-figures-gallery-dot]');
      if (dot && root.contains(dot) && dot.getAttribute('aria-current') !== 'true') {
        trackEngage(root, 'dot', 'group');
        return;
      }

      const previous = event.target.closest(SELECTORS.previous);
      if (previous && root.contains(previous) && !previous.disabled && canMove(track, 'previous')) {
        trackEngage(root, 'arrow', 'previous');
        return;
      }

      const next = event.target.closest(SELECTORS.next);
      if (next && root.contains(next) && !next.disabled && canMove(track, 'next')) {
        trackEngage(root, 'arrow', 'next');
      }
    }, true);

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' && canMove(track, 'previous')) {
        trackEngage(root, 'keyboard', 'previous');
      } else if (event.key === 'ArrowRight' && canMove(track, 'next')) {
        trackEngage(root, 'keyboard', 'next');
      } else if (event.key === 'Home' && canMove(track, 'previous')) {
        trackEngage(root, 'keyboard', 'previous');
      } else if (event.key === 'End' && canMove(track, 'next')) {
        trackEngage(root, 'keyboard', 'next');
      }
    }, true);

    if ('PointerEvent' in window) {
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') {
          setPendingInteraction(root, track, 'touch');
        }
      }, { passive: true });
    } else {
      track.addEventListener('touchstart', () => {
        setPendingInteraction(root, track, 'touch');
      }, { passive: true });
    }

    track.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > 2 || event.shiftKey) {
        setPendingInteraction(root, track, 'wheel');
      }
    }, { passive: true });

    track.addEventListener('scroll', () => {
      handleScrollInteraction(root, track);
    }, { passive: true });
  }

  function initGallery(root) {
    if (!root || initialised.has(root)) return;

    initialised.add(root);
    root.dataset.figuresGalleryAnalyticsInitialised = 'true';

    track(root, 'figures_gallery_present');
    initViewTracking(root);
    initInteractionTracking(root);
  }

  function boot(scope) {
    const root = scope || document;

    if (root instanceof Element && root.matches(SELECTORS.root)) {
      initGallery(root);
    }

    root.querySelectorAll?.(SELECTORS.root).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });
})();


